import type {
    CatalogueEngagementRingFilterInput,
    CatalogueEngagementRingInputFragment,
    CatalogueEngagementRingSignatureStoneInput,
    FilterShapeFragment,
    MetalCode,
    MetalFilterFragment,
    NonSkuGemstoneFilterInput,
    NonSkuGemstoneFragment,
    NonSkuLabGrownDiamondFilterInput,
    NonSkuLabGrownFragment,
    SignatureStoneInputFragment,
    StoneFragment,
    StoneShapeCode,
} from '@/graphql/api'
import type {
    RingBuilderRingTypes,
    RingBuilderStoneTypes,
} from '@/domain/product/ring/ring-builder'
import type { NonSkuParams } from '@/views/product/engagement-ring/composables/nonSkuParams'
import { assertNever } from '@/helpers'
import type { EngagementRingProps } from '@/views/product/engagement-ring/er.ts'

export const compareMetalFilterFragmentToMetalInput = (
    filter: MetalFilterFragment,
    input: MetalCode
): boolean => filter.metal.systemCode === input

export function getCatalogueEngagementRingFilterInput(
    ring: CatalogueEngagementRingInputFragment
): CatalogueEngagementRingFilterInput {
    return {
        centreStoneShape: ring.centreStoneConstraints.shape.systemCode,
        slug: ring.slug,
        primaryMetalCode: ring.primaryMetal.systemCode,
        suffixSlug: ring.suffixSlug,
    }
}

export const filterMapperLabel = <
    I extends string,
    O extends {
        label: {
            systemCode: I
            // translation: { name: string }
            // icon: { url: string } | null
        }
        totalResults: number
    },
>(
    item: O
): I => item.label.systemCode

export const fliterMapperMetal = (item: MetalFilterFragment): MetalCode =>
    item.metal.systemCode

export const filterMapperShape = (item: FilterShapeFragment): StoneShapeCode =>
    item.shape.systemCode

export const nonSkuLabGrownDiamondToFilterInput = (
    stone: NonSkuLabGrownFragment
): NonSkuLabGrownDiamondFilterInput => {
    return {
        carat: stone.caratWeight,
        clarity: stone.clarity.systemCode,
        color: stone.color.systemCode,
        shape: stone.shape.systemCode,
    }
}

export const nonSkuLabGemstoneToFilterInput = (
    stone: NonSkuGemstoneFragment
): NonSkuGemstoneFilterInput => {
    return {
        carat: stone.caratWeight,
        clarity: stone.clarity.systemCode,
        color: stone.color.systemCode,
        crystal: stone.crystal.systemCode,
        grade: stone.colorGrade.systemCode,
        shape: stone.shape.systemCode,
    }
}

export const gemsApiStoneToInput = (stone: StoneFragment): string => {
    return stone.identifier
}

export const getEngagementRingFilterInputFromProps = (
    props: EngagementRingProps
): CatalogueEngagementRingFilterInput => ({
    centreStoneShape: props.shape,
    slug: props.slug,
    primaryMetalCode: props.metal,
    suffixSlug: props.suffixSlug,
    grande: props.isGrande,
})

export function signatureStoneToInput(
    stone: SignatureStoneInputFragment
): CatalogueEngagementRingSignatureStoneInput {
    return {
        color: stone.color.systemCode,
        crystal: stone.crystal.systemCode,
        width: stone.width,
    }
}

export function createSignatureStoneFinderPredicate(
    input: CatalogueEngagementRingSignatureStoneInput
) {
    return <T extends SignatureStoneInputFragment>(stone: T): boolean => {
        return (
            stone.color.systemCode === input.color &&
            stone.crystal.systemCode === input.crystal &&
            stone.width === input.width
        )
    }
}

export function isSameRing(
    ring: RingBuilderRingTypes | null,
    props: EngagementRingProps
): boolean {
    return (
        !!ring &&
        props.slug === ring.slug &&
        props.suffixSlug === ring.suffixSlug &&
        props.shape === ring.centreStoneConstraints.shape.systemCode &&
        props.metal === ring.primaryMetal.systemCode
    )
}

export function isSameStone(
    stone: RingBuilderStoneTypes | null,
    nonSkuParams: NonSkuParams
): boolean {
    if (!stone) {
        return false
    }

    switch (nonSkuParams.type) {
        case 'lab-grown':
            return (
                stone.__typename === 'NonSkuLabGrown' &&
                nonSkuParams.params.nonSkuLabGrownFilter.color ===
                    stone.color.systemCode &&
                nonSkuParams.params.nonSkuLabGrownFilter.shape ===
                    stone.shape.systemCode &&
                nonSkuParams.params.nonSkuLabGrownFilter.clarity ===
                    stone.clarity.systemCode &&
                compareCaratWeight(
                    nonSkuParams.params.nonSkuLabGrownFilter.carat,
                    stone.caratWeight
                )
            )
        case 'gemstone':
            return (
                stone.__typename === 'NonSkuGemstone' &&
                nonSkuParams.params.nonSkuGemStoneFilter.shape ===
                    stone.shape.systemCode &&
                nonSkuParams.params.nonSkuGemStoneFilter.color ===
                    stone.color.systemCode &&
                nonSkuParams.params.nonSkuGemStoneFilter.clarity ===
                    stone.clarity.systemCode &&
                nonSkuParams.params.nonSkuGemStoneFilter.crystal ===
                    stone.crystal.systemCode &&
                nonSkuParams.params.nonSkuGemStoneFilter.grade ===
                    stone.colorGrade.systemCode &&
                compareCaratWeight(
                    nonSkuParams.params.nonSkuGemStoneFilter.carat,
                    stone.caratWeight
                )
            )
        case 'natural':
            // Preset stones aren't exposed to the frontend, so only a SKU can match
            return (
                stone.__typename === 'GemsApi_Stone' &&
                stone.identifier === nonSkuParams.params.sku
            )
        default:
            return assertNever(nonSkuParams, false)
    }
}

function compareCaratWeight(
    first: string | number,
    second: string | number
): boolean {
    const firstNumber = Number(first)
    const secondNumber = Number(second)

    if (isNaN(firstNumber)) {
        return false
    }

    if (isNaN(secondNumber)) {
        return false
    }

    return firstNumber.toFixed(6) === secondNumber.toFixed(6)
}
